import { NavLink, useLocation } from "react-router-dom"
import { SVGIcon } from "~/components"
import { useTranslate } from "~/ur-react-hooks"
import { cn } from "~/utils/classnames"
import { captureWorksheetConfigChange } from "../analytics"
import { useTourOptional } from "../context/TourContext"
import GeneratorHelpButton from "./GeneratorHelpButton"

export type GeneratorMainFrameProps = {
  basePath?: string
}

const SUBJECT_BY_TAB = {
  mathematics: "MATHEMATICS",
  norwegian: "NORWEGIAN",
  english: "ENGLISH",
} as const

function getSubjectFromPath(pathname: string) {
  const pathParts = pathname.split("/").filter(Boolean)
  const tab = pathParts[pathParts.length - 1]
  if (!tab) {
    return null
  }
  return SUBJECT_BY_TAB[tab as keyof typeof SUBJECT_BY_TAB] ?? null
}

const GeneratorMainFrame = ({
  basePath = "/generator",
}: GeneratorMainFrameProps) => {
  const tour = useTourOptional()
  const location = useLocation()
  const currentSubject = getSubjectFromPath(location.pathname)

  const translations = useTranslate({
    showOptions: "worksheets.buttons.show-options",
    hideOptions: "worksheets.buttons.hide-options",
    mathematics: "common.mathematics",
    norwegian: "common.norwegian",
    english: "common.english",
  })

  const trackSubjectTabChange = (
    nextSubject: (typeof SUBJECT_BY_TAB)[keyof typeof SUBJECT_BY_TAB],
  ) => {
    if (!currentSubject || currentSubject === nextSubject) {
      return
    }

    captureWorksheetConfigChange(
      {
        subject: nextSubject,
      },
      "subject",
      currentSubject,
      nextSubject,
    )
  }

  return (
    <div className="flex flex-col w-full">
      <nav
        data-tour="subject-tabs"
        className={
          "relative z-10 flex w-full border-b border-gray-200 bg-white"
        }
      >
        <NavLink
          to={`${basePath}/mathematics`}
          onClick={() => trackSubjectTabChange("MATHEMATICS")}
          className={({ isActive }) =>
            cn(
              "flex-1 px-4 py-3 text-center text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors",
              isActive && "border-b-2 border-primary text-primary",
            )
          }
        >
          {({ isActive }) => (
            <span className={"flex items-center justify-center gap-2"}>
              <SVGIcon
                icon={"Math"}
                className={cn(
                  "w-4 h-4",
                  isActive ? "fill-primary" : "fill-gray-400",
                )}
              />
              {translations.mathematics}
            </span>
          )}
        </NavLink>
        <NavLink
          to={`${basePath}/norwegian`}
          onClick={() => trackSubjectTabChange("NORWEGIAN")}
          className={({ isActive }) =>
            cn(
              "flex-1 px-4 py-3 text-center text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors",
              isActive && "border-b-2 border-primary text-primary",
            )
          }
        >
          {({ isActive }) => (
            <span className={"flex items-center justify-center gap-2"}>
              <SVGIcon
                icon={"Books"}
                className={cn(
                  "w-4 h-4",
                  isActive ? "fill-primary" : "fill-gray-400",
                )}
              />
              {translations.norwegian}
            </span>
          )}
        </NavLink>
        <NavLink
          to={`${basePath}/english`}
          onClick={() => trackSubjectTabChange("ENGLISH")}
          className={({ isActive }) =>
            cn(
              "flex-1 px-4 py-3 text-center text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors",
              isActive && "border-b-2 border-primary text-primary",
            )
          }
        >
          {({ isActive }) => (
            <span className={"flex items-center justify-center gap-2"}>
              <SVGIcon
                icon={"Globe"}
                className={cn(
                  "w-4 h-4",
                  isActive ? "fill-primary" : "fill-gray-400",
                )}
              />
              {translations.english}
            </span>
          )}
        </NavLink>
        {tour && (
          <div className="flex items-center px-2">
            <GeneratorHelpButton
              onClick={() => {
                tour.stopTour()
                tour.resetTour()
                // Small delay to ensure clean state before restarting
                setTimeout(() => tour.startTour(), 50)
              }}
            />
          </div>
        )}
      </nav>
    </div>
  )
}

export default GeneratorMainFrame
